// ---------------------------------------------------------------------------
// Estilos do "Enviar multiplos meses".
// ---------------------------------------------------------------------------
// Filosofia: usa as PRIMITIVAS centralizadas da app (``Dialog``,
// ``ModalHeader``, ``DialogContent``, ``DialogActions``,
// ``CustomButtons/Button``). Os estilos abaixo cuidam APENAS do conteudo
// interno do modal (stepper, listas, grade de meses, linhas de upload,
// pills). Header dark, botao de fechar e geometria do dialog vem do
// ``ModalHeader`` + ``Dialog`` — assim a UX casa com
// ``ResetInputsDialog``, ``InputNotesDialog``, ``ProgressDashboardModal``
// e demais modais V3.
// ---------------------------------------------------------------------------

import { makeStyles } from "@material-ui/core/styles";
import {
  grayColor,
  whiteColor,
  successColor,
  warningColor,
  dangerColor,
  infoColor,
  blendColors,
} from "../../../../assets/jss/material-dashboard-pro-react";

// Cores secundarias usadas em chips/pontos de overlap. Hardcoded como em
// ``ProgressDashboardModal`` (mesmas constantes semanticas da V3).
const WARN = warningColor[0]; // laranja do tema
const ERR = dangerColor[0];

const useMultiMonthStyles = makeStyles((theme) => ({
  // ----- Dialog shell ---------------------------------------------------
  // ``paper`` controla o cartao branco do dialog. Largura propria pra
  // este modal (entre ``sm`` e ``md``); altura fluida com teto.
  paper: {
    borderRadius: 10,
    width: 760,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "92vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  // ----- Body / footer --------------------------------------------------
  // ``body`` cobre o miolo do dialog (stepper + conteudo do passo). O
  // wrapper ``stepBody`` interno garante geometria estavel mesmo em
  // sub-estados (loading/overlap/etc.) — overflow auto so na sublista.
  body: {
    padding: 0,
    background: whiteColor,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    flex: 1,
    overflow: "hidden",
    // ``DialogContent`` injeta padding default — anulamos pra controlar
    // por dentro (cada step define o proprio).
    "&:first-child": { paddingTop: 0 },
  },
  stepBodyWrap: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    padding: "16px 20px",
    display: "flex",
    flexDirection: "column",
  },
  footer: {
    padding: "10px 16px",
    margin: 0,
    borderTop: `1px solid ${grayColor[11]}`,
    background: grayColor[19],
    display: "flex",
    alignItems: "center",
    // Espaco entre o grupo da esquerda (Voltar) e o da direita
    // (Cancelar/Avancar). Padroniza com a ergonomia de wizards.
    "& > .footerLeft": { marginRight: "auto" },
  },

  // ----- Stepper (1 / 2 / 3) -------------------------------------------
  // Cor do numero ativo = ``clientColor``; pendente = cor do cliente
  // "diluida" (clientColorLight). Done = clientColor solido com check.
  stepper: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "stretch",
    borderBottom: `1px solid ${grayColor[11]}`,
    background: whiteColor,
  },
  step: {
    flex: 1,
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    position: "relative",
    "& + &::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      width: 1,
      height: 26,
      background: grayColor[11],
      transform: "translateY(-50%)",
    },
  },
  stepNum: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 500,
    flex: "0 0 auto",
    background: (props) =>
      blendColors(props.clientColor || ERR, whiteColor, 0.18),
    color: (props) => blendColors(props.clientColor || ERR, whiteColor, 0.55),
  },
  stepLbl: { display: "flex", flexDirection: "column", lineHeight: 1.25 },
  stepLblT: {
    fontSize: 13,
    color: grayColor[6],
    fontWeight: 400,
  },
  stepLblH: { fontSize: 11, color: grayColor[0] },
  stepCurrent: {
    "& $stepNum": {
      background: (props) => props.clientColor || ERR,
      color: theme.palette.custom?.clientTextColor || whiteColor,
    },
    "& $stepLblT": { color: grayColor[2], fontWeight: 500 },
  },
  stepDone: {
    "& $stepNum": {
      background: (props) => props.clientColor || ERR,
      color: theme.palette.custom?.clientTextColor || whiteColor,
    },
    "& $stepLblT": { color: grayColor[2] },
  },
  stepPending: {},

  // ----- Section title --------------------------------------------------
  sectionTitle: {
    fontSize: 11,
    fontWeight: 500,
    color: grayColor[2],
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 8,
  },

  // ----- Step 1: arquivos -----------------------------------------------
  filePickers: {
    display: "flex",
    gap: 8,
    marginBottom: 8,
    alignItems: "center",
  },
  filePickerSpacer: { flex: 1 },
  // Botoes "Selecionar pasta" / "Selecionar arquivos" — mesmo visual
  // do ``btnGhost`` do drawer (que e' o "Enviar multiplos meses"), so
  // que com fonte/padding compactos pra caber bem na linha do step 1.
  filePickerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    color: grayColor[1],
    border: `1px solid ${grayColor[5]}`,
    borderRadius: 3,
    padding: "5px 10px",
    fontFamily: "inherit",
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    cursor: "pointer",
    transition: "all 200ms ease",
    "& .MuiSvgIcon-root": { fontSize: 14 },
    "&:hover": { background: grayColor[12] },
    "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
  },
  fileLinkBtn: {
    background: "transparent",
    border: "none",
    padding: 0,
    fontFamily: "inherit",
    fontSize: 12,
    cursor: "pointer",
    color: (props) => props.clientColor || ERR,
    "&:hover": { textDecoration: "underline" },
  },
  fileList: {
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 6,
    height: 140,
    background: whiteColor,
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 12px",
    fontSize: 12,
    color: grayColor[2],
    borderBottom: `1px solid ${grayColor[11]}`,
    "&:last-child": { borderBottom: "none" },
  },
  fileName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileSize: { color: grayColor[6], fontSize: 11, flex: "0 0 auto" },
  fileRemove: {
    background: "transparent",
    border: "none",
    color: grayColor[6],
    fontSize: 16,
    cursor: "pointer",
    padding: "0 4px",
    lineHeight: 1,
    "&:hover": { color: ERR },
  },
  fileEmpty: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: grayColor[6],
    fontSize: 12,
  },
  fileMeta: {
    marginTop: 6,
    fontSize: 11,
    color: grayColor[6],
    display: "flex",
    justifyContent: "space-between",
  },

  // ----- Step 1: SINGLE — chip do arquivo selecionado ------------------
  // No modo SINGLE substituimos o box scrollavel + meta line por uma
  // linha compacta. Vazio: hint discreto; selecionado: chip com nome,
  // tamanho e botao de remover. Mantem ergonomia visual semelhante a
  // pills de selects/breadcrumb usados em outros pontos da V3.
  singleFileLine: {
    marginTop: 4,
    minHeight: 28,
    display: "flex",
    alignItems: "center",
  },
  singleFileLineEmpty: {
    fontSize: 12,
    color: grayColor[6],
    fontStyle: "italic",
  },
  singleFileChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    maxWidth: "100%",
    minWidth: 0,
    padding: "5px 6px 5px 10px",
    background: grayColor[19],
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 6,
    fontSize: 12,
    color: grayColor[2],
  },
  singleFileChipName: {
    flex: "0 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  singleFileChipSize: {
    flex: "0 0 auto",
    fontSize: 11,
    color: grayColor[6],
  },
  singleFileChipRemove: {
    flex: "0 0 auto",
    background: "transparent",
    border: "none",
    color: grayColor[6],
    fontSize: 16,
    cursor: "pointer",
    padding: "0 4px",
    lineHeight: 1,
    borderRadius: 3,
    "&:hover": { color: ERR, background: blendColors(ERR, whiteColor, 0.06) },
  },

  // ----- Step 1: month grid --------------------------------------------
  monthGridWrap: {
    marginTop: 16,
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "column",
  },
  monthGridArea: { flex: "0 0 auto" },
  monthGrid: {
    borderCollapse: "separate",
    borderSpacing: "3px",
    width: "100%",
    tableLayout: "fixed",
    userSelect: "none",
  },
  yearLabel: {
    textAlign: "center",
    fontSize: 12,
    color: grayColor[6],
    fontWeight: 400,
    height: 26,
  },
  cell: {
    height: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 2,
    fontSize: 12,
    background: whiteColor,
    color: grayColor[1],
    border: `1px solid ${grayColor[8]}`,
    cursor: "pointer",
    position: "relative",
    transition: "background 0.1s",
  },
  cellSelected: {
    background: (props) => props.clientColor || ERR,
    color: theme.palette.custom?.clientTextColor || whiteColor,
    borderColor: (props) => props.clientColor || ERR,
    fontWeight: 400,
  },
  cellAlreadyFilled: {
    "&::after": {
      content: '""',
      width: 4,
      height: 4,
      borderRadius: "50%",
      background: WARN,
      position: "absolute",
      top: 3,
      right: 3,
    },
  },
  monthSummary: {
    marginTop: 10,
    padding: "6px 10px",
    background: grayColor[19],
    borderRadius: 4,
    fontSize: 11,
    color: grayColor[6],
    display: "flex",
    alignItems: "center",
    gap: 6,
    minHeight: 28,
    "& strong": { color: (props) => props.clientColor || ERR },
  },
  monthSummaryRight: { marginLeft: "auto", color: grayColor[8] },
  overlapWarn: {
    marginTop: 10,
    padding: "8px 12px",
    background: blendColors(WARN, whiteColor, 0.08),
    borderLeft: `3px solid ${WARN}`,
    borderRadius: 4,
    fontSize: 12,
    color: "#5a3700",
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    flex: "0 0 auto",
  },
  overlapWarnOff: { display: "none" },

  // ----- Step 2: match table -------------------------------------------
  matchArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 6,
    overflow: "hidden",
    background: whiteColor,
    transition: "opacity 200ms ease",
  },
  matchAreaLoading: {
    opacity: 0.35,
    pointerEvents: "none",
    userSelect: "none",
    filter: "grayscale(0.2)",
  },
  matchHead: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr) 130px",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    background: grayColor[19],
    borderBottom: `1px solid ${grayColor[11]}`,
    fontSize: 11,
    color: grayColor[0],
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  matchHeadConf: { textAlign: "center" },
  matchBody: { flex: 1, minHeight: 0 },
  matchRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr) 130px",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    borderBottom: `1px solid ${grayColor[11]}`,
    fontSize: 12,
    "&:last-child": { borderBottom: "none" },
  },
  matchRowUnmatched: { background: blendColors(WARN, whiteColor, 0.04) },
  matchRowDupe: { background: blendColors(ERR, whiteColor, 0.04) },
  matchFileName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: grayColor[2],
  },
  matchSelectWrap: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
  },
  matchSelect: {
    flex: 1,
    minWidth: 0,
    padding: "5px 8px",
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 4,
    fontSize: 12,
    background: whiteColor,
    color: grayColor[2],
    fontFamily: "inherit",
  },
  matchSelectDupe: {
    borderColor: ERR,
    background: blendColors(ERR, whiteColor, 0.04),
  },
  conf: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 400,
    justifySelf: "center",
    whiteSpace: "nowrap",
  },
  confHigh: {
    background: blendColors(successColor[0], whiteColor, 0.12),
    color: successColor[0],
  },
  confMedium: {
    background: blendColors(WARN, whiteColor, 0.12),
    color: WARN,
  },
  confManual: {
    background: blendColors(infoColor[0], whiteColor, 0.12),
    color: infoColor[0],
  },
  confNone: { background: blendColors(ERR, whiteColor, 0.12), color: ERR },
  matchSummary: {
    marginTop: 10,
    fontSize: 12,
    color: grayColor[0],
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 18,
    flex: "0 0 auto",
  },
  matchSummaryDupe: { color: ERR },

  // ----- Step 3: upload -------------------------------------------------
  uploadCounters: {
    display: "flex",
    gap: 24,
    padding: "10px 14px",
    background: grayColor[19],
    borderRadius: 6,
    marginBottom: 12,
    alignItems: "center",
    flex: "0 0 auto",
  },
  stat: { display: "flex", flexDirection: "column", gap: 1 },
  statV: { fontSize: 18, fontWeight: 500, lineHeight: 1 },
  statL: {
    fontSize: 10,
    color: grayColor[6],
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  statSuccess: { "& $statV": { color: successColor[0] } },
  statError: { "& $statV": { color: ERR } },
  statWaiting: { "& $statV": { color: grayColor[0] } },
  countersRight: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 12,
    color: grayColor[0],
  },

  uploadList: {
    flex: 1,
    minHeight: 0,
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 6,
    background: whiteColor,
  },
  uploadRow: {
    display: "grid",
    // Nome do arquivo (2.4fr) ganha o espaco que tiramos da barra de
    // progresso (0.8fr, -20% vs. anterior). Min da barra cai 120 → 96
    // pelo mesmo motivo. Largura minima da coluna nome garantida pelo
    // flex interno do ``uploadInfo``, que ainda preserva o pill do mes.
    gridTemplateColumns: "minmax(260px, 2.4fr) minmax(96px, 0.8fr) 60px 26px",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    borderBottom: `1px solid ${grayColor[11]}`,
    fontSize: 12,
    "&:last-child": { borderBottom: "none" },
  },
  uploadInfo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  uploadFile: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: grayColor[2],
  },
  uploadTarget: {
    fontSize: 11,
    color: grayColor[0],
    padding: "1px 7px",
    background: grayColor[19],
    borderRadius: 10,
    flex: "0 0 auto",
  },
  progressBar: {
    width: "100%",
    height: 6,
    // grayColor[10] (#e4e4e4) — cinza claro o suficiente pra dar
    // contraste com o fundo branco da row sem competir com o fill.
    background: grayColor[10],
    borderRadius: 3,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: (props) => props.clientColor || ERR,
    transition: "width 200ms ease",
  },
  // Animacao "progresso infinito" — usada como fallback enquanto a
  // primeira tick do ``onUploadProgress`` nao chegou (progress === 0).
  // Faixa de ~30% deslizando da esquerda pra direita, em loop.
  "@keyframes multiMonthIndeterminate": {
    "0%": { transform: "translateX(-100%)" },
    "100%": { transform: "translateX(400%)" },
  },
  progressFillIndeterminate: {
    width: "30% !important",
    animation: "$multiMonthIndeterminate 1.1s ease-in-out infinite",
  },
  pct: {
    fontSize: 11,
    color: grayColor[0],
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  uploadIcon: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    justifySelf: "center",
  },
  rowWaiting: {
    "& $progressFill": { background: grayColor[8], width: "0% !important" },
    "& $uploadIcon": { background: grayColor[12], color: grayColor[0] },
  },
  rowUploading: {
    "& $uploadIcon": {
      background: (props) =>
        blendColors(props.clientColor || ERR, whiteColor, 0.12),
      color: (props) => props.clientColor || ERR,
    },
  },
  rowSuccess: {
    "& $progressFill": { background: successColor[0] },
    "& $uploadIcon": {
      background: blendColors(successColor[0], whiteColor, 0.12),
      color: successColor[0],
    },
  },
  rowError: {
    "& $progressFill": { background: ERR, width: "100% !important" },
    "& $uploadIcon": {
      background: blendColors(ERR, whiteColor, 0.12),
      color: ERR,
    },
  },
  rowInterrupted: {
    "& $progressFill": { background: grayColor[6] },
    "& $uploadIcon": { background: grayColor[19], color: grayColor[6] },
  },

  // ----- Header right slot (spinner ON apenas em loading/upload) ------
  headerSpinnerSlot: {
    width: 38,
    height: 12,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    visibility: "hidden",
  },
  headerSpinnerOn: { visibility: "visible" },

  // ----- Header mode pill (segmented "Multiplos arquivos / Unico") ----
  // Vive em ``rightContent`` do ``ModalHeader``, antes do spinner.
  // Visual: track translucido sobre o header escuro, com a opcao ativa
  // ganhando fundo branco/quase-branco semi-transparente. Sem hover
  // pra nao mover a cor do header. Acessivel via teclado (botoes).
  modePill: {
    display: "inline-flex",
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.10)",
    borderRadius: 999,
    padding: 2,
    border: "1px solid rgba(255, 255, 255, 0.15)",
  },
  modePillBtn: {
    background: "transparent",
    border: "none",
    color: "rgba(255, 255, 255, 0.7)",
    fontFamily: "inherit",
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.02em",
    padding: "4px 12px",
    borderRadius: 999,
    cursor: "pointer",
    transition: "background 180ms ease, color 180ms ease",
    "&:hover": { color: whiteColor },
    "&:disabled": { cursor: "not-allowed", opacity: 0.45 },
  },
  modePillBtnActive: {
    background: "rgba(255, 255, 255, 0.92)",
    color: grayColor[1],
    "&:hover": { color: grayColor[1] },
  },

  // ----- Step UPLOAD (modo SINGLE) ------------------------------------
  // Substitui a lista de rows por uma "card" central com barra de
  // progresso, status textual e contador de meses. Mais simples e
  // honesto: existe uma so chamada HTTP, entao nao faz sentido fingir
  // que ha N uploads paralelos.
  singleUploadCard: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px",
    gap: 18,
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 6,
    background: whiteColor,
  },
  singleUploadIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 26,
    transition: "background 200ms ease, color 200ms ease",
  },
  singleIconUploading: {
    background: (props) =>
      blendColors(props.clientColor || ERR, whiteColor, 0.12),
    color: (props) => props.clientColor || ERR,
  },
  singleIconSuccess: {
    background: blendColors(successColor[0], whiteColor, 0.12),
    color: successColor[0],
  },
  singleIconError: {
    background: blendColors(ERR, whiteColor, 0.12),
    color: ERR,
  },
  singleUploadText: {
    fontSize: 14,
    color: grayColor[2],
    textAlign: "center",
    fontWeight: 500,
  },
  singleUploadSub: {
    fontSize: 12,
    color: grayColor[6],
    textAlign: "center",
    marginTop: -10,
  },
  singleProgressBar: {
    width: "60%",
    minWidth: 240,
    height: 6,
    background: grayColor[10],
    borderRadius: 3,
    overflow: "hidden",
  },
  singleProgressFill: {
    height: "100%",
    background: (props) => props.clientColor || ERR,
    transition: "width 200ms ease",
  },
  singleProgressFillSuccess: { background: successColor[0] },
  singleProgressFillError: { background: ERR },

  // ----- Scrollbar discreta (mesmo padrao de inputs_v3_components) ----
  scroll: {
    overflow: "auto",
    "@supports (overflow: overlay)": { overflow: "overlay" },
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": { scrollbarColor: `${grayColor[6]} transparent` },
    "&::-webkit-scrollbar": { width: "6px", height: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: 3,
      transition: "background 0.2s ease",
    },
    "&:hover::-webkit-scrollbar-thumb": { background: grayColor[6] },
  },

  // ----- Botao do drawer (vive na InputDrawerV3.js) -------------------
  // Mantido aqui pra centralizar visual; replica o ``btnGhost`` da
  // toolbar do drawer com cor do cliente. NAO usado no momento (o
  // drawer ja tem ``classes.btn classes.btnGhost`` proprios) — fica
  // disponivel caso queiramos uma variante mais "branded" no futuro.
  toolbarBtnMM: {},
}));

export default useMultiMonthStyles;
